import { ICustomEventData } from '@modules/src/interfaces/IComponents'
import { BaseContainer } from '@base/base/BaseContainer'
import Facade from '../../Facade'
import GameSignals from '../../signals/GameSignals'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'
import { toMajorUnits } from '@/utils/CurrencyUnits'

// --- Types ---

export type TransformSnapshot = {
    x: number
    y: number
    scaleX: number
    scaleY: number
}

export type FreeSpinsCounterData = {
    remaining: number
    total: number
}

// --- Orientation / state helpers ---

export const isPortrait = (): boolean => String(Facade.orientation).toUpperCase() === 'PORTRAIT'

export const isFreeSpins = (): boolean => Boolean(Facade.model?.roundState?.isFreeSpins)

export const getBaseBackgroundAnimation = (): string => (isPortrait() ? 'MG_P' : 'MG_L')

export const getFreeSpinsBackgroundAnimation = (): string => (isPortrait() ? 'FS_P' : 'FS_L')

// --- Layout constants ---

export const FREE_SPINS_MACHINE_FRAME_LANDSCAPE = {
    x: 531,
    y: 24,
    scaleX: 0.625,
    scaleY: 0.62,
}

export const FREE_SPINS_GAME_CONTAINER_LANDSCAPE = {
    x: 1089.5,
    y: 484.5,
    scale: 0.9,
}

// --- Private layout state ---

let baseMachineFrameLandscapeTransform: TransformSnapshot | null = null
let baseGameContainerLandscapeTransform: TransformSnapshot | null = null

// --- Layout functions ---

export const saveBaseTransforms = (machineFrame?: BaseContainer) => {
    if (!isPortrait() && machineFrame && baseMachineFrameLandscapeTransform == null) {
        baseMachineFrameLandscapeTransform = {
            x: machineFrame.x,
            y: machineFrame.y,
            scaleX: machineFrame.scale.x,
            scaleY: machineFrame.scale.y,
        }
    }

    if (!isPortrait() && Facade.container && baseGameContainerLandscapeTransform == null) {
        baseGameContainerLandscapeTransform = {
            x: Facade.container.x,
            y: Facade.container.y,
            scaleX: Facade.container.scale.x,
            scaleY: Facade.container.scale.y,
        }
    }
}

export const applyFreeSpinsLayout = (machineFrame?: BaseContainer) => {
    if (isPortrait()) {
        return
    }

    saveBaseTransforms(machineFrame)

    if (machineFrame) {
        machineFrame.position.set(FREE_SPINS_MACHINE_FRAME_LANDSCAPE.x, FREE_SPINS_MACHINE_FRAME_LANDSCAPE.y)
        machineFrame.scale.set(FREE_SPINS_MACHINE_FRAME_LANDSCAPE.scaleX, FREE_SPINS_MACHINE_FRAME_LANDSCAPE.scaleY)
    }

    if (Facade.container) {
        Facade.container.position.set(FREE_SPINS_GAME_CONTAINER_LANDSCAPE.x, FREE_SPINS_GAME_CONTAINER_LANDSCAPE.y)
        Facade.container.scale.set(FREE_SPINS_GAME_CONTAINER_LANDSCAPE.scale)
    }
}

export const restoreBaseLayout = (machineFrame?: BaseContainer) => {
    if (isPortrait()) {
        return
    }

    saveBaseTransforms(machineFrame)

    if (machineFrame && baseMachineFrameLandscapeTransform) {
        machineFrame.position.set(baseMachineFrameLandscapeTransform.x, baseMachineFrameLandscapeTransform.y)
        machineFrame.scale.set(baseMachineFrameLandscapeTransform.scaleX, baseMachineFrameLandscapeTransform.scaleY)
    }

    if (Facade.container && baseGameContainerLandscapeTransform) {
        Facade.container.position.set(baseGameContainerLandscapeTransform.x, baseGameContainerLandscapeTransform.y)
        Facade.container.scale.set(
            baseGameContainerLandscapeTransform.scaleX,
            baseGameContainerLandscapeTransform.scaleY
        )
    }
}

// --- FS counter ---

export const getFreeSpinsCounterData = (signalData?: any): FreeSpinsCounterData | null => {
    const isFreeSpinsSession = Boolean(signalData?.isFreeSpins ?? Facade.model?.roundState?.isFreeSpins)

    if (!isFreeSpinsSession) {
        return null
    }

    const freebet = signalData?.freebet || Facade.model?.spinData?.freebet

    if (!freebet) {
        return null
    }

    const remainingRaw = Number(freebet.amount_left)
    const totalRaw = Number(freebet.total_amount_given)

    const remaining = Number.isFinite(remainingRaw) ? Math.max(0, remainingRaw) : 0
    const total = Number.isFinite(totalRaw) ? Math.max(0, totalRaw) : 0

    if (remaining === 0 && total === 0) {
        return null
    }

    return {
        remaining,
        total: Math.max(total, remaining),
    }
}

export const getFreeSpinsCounterText = (signalData?: any): string => {
    const data = getFreeSpinsCounterData(signalData)

    if (!data) {
        return ''
    }

    return `${data.remaining}/${data.total}`
}

export const FS_COUNTER_LABEL_CHILD_NAME = 'FS_COUNTER_LABEL_CHILD'
export const FS_COUNTER_VALUE_CHILD_NAME = 'FS_COUNTER_VALUE_CHILD'

export const FS_TOP_TUMBLE_LABEL_CHILD_NAME = 'FS_TOP_TUMBLE_LABEL_CHILD'
export const FS_TOP_TUMBLE_VALUE_CHILD_NAME = 'FS_TOP_TUMBLE_VALUE_CHILD'
export const FS_TOP_MULTIPLIER_LABEL_CHILD_NAME = 'FS_TOP_MULTIPLIER_LABEL_CHILD'
export const FS_TOP_MULTIPLIER_VALUE_CHILD_NAME = 'FS_TOP_MULTIPLIER_VALUE_CHILD'
export const FS_TOP_TOTAL_LABEL_CHILD_NAME = 'FS_TOP_TOTAL_LABEL_CHILD'
export const FS_TOP_TOTAL_VALUE_CHILD_NAME = 'FS_TOP_TOTAL_VALUE_CHILD'

// --- FS top info state ---

let fsCurrentSpinTumbleWin = 0
let fsCurrentSpinMultiplier = 1
let fsCumulativeTotalWin = 0
let fsCurrentSpinTotalIncrement = 0
let fsDisplayedTumbleWin = 0
let fsDisplayedTotalWin = 0
let fsDisplayedMultiplier = 1
let fsMultiplierCollectPreview = 0
let fsTumbleTween: gsap.core.Tween | null = null
let fsTotalTween: gsap.core.Tween | null = null
let fsMultiplierTween: gsap.core.Tween | null = null
let fsMultiplierFlyTweens: gsap.core.Tween[] = []

export const AVAILABLE_MULTIPLIER_TEXTURES: number[] = [2, 3, 4, 5, 6, 8, 10, 15, 20, 25, 50, 100]

export const normalizeMultiplierTextureValue = (value: number): number => {
    return AVAILABLE_MULTIPLIER_TEXTURES.includes(value) ? value : 2
}

export const FS_TOP_COUNTUP_DURATION = 0.5
export const FS_TOP_COUNTUP_EASING = 'power2.out'

// --- FS UI text helpers ---

export const ensureFreeSpinsCounterTexts = (container: PIXI.Container) => {
    let label = container.getChildByName(FS_COUNTER_LABEL_CHILD_NAME) as PIXI.Text
    let value = container.getChildByName(FS_COUNTER_VALUE_CHILD_NAME) as PIXI.Text

    if (!label) {
        label = new PIXI.Text('FREE SPINS', {
            fontFamily: 'Montserrat_Bold',
            fontSize: 42,
            fill: 0xffffff,
            stroke: {
                color: '#3f1f02',
                width: 8,
            },
            letterSpacing: 2,
        })
        label.name = FS_COUNTER_LABEL_CHILD_NAME
        label.anchor.set(0.5)
        label.position.set(303, 154.5)
        container.addChild(label)
    }

    if (!value) {
        value = new PIXI.Text('', {
            fontFamily: 'Montserrat_Bold',
            fontSize: 58,
            fill: 0xfff27a,
            stroke: {
                color: '#5d0115',
                width: 10,
            },
        })
        value.name = FS_COUNTER_VALUE_CHILD_NAME
        value.anchor.set(0.5)
        value.position.set(303, 219)
        container.addChild(value)
    }

    return { label, value }
}

export const updateFreeSpinsCounterTexts = (container: PIXI.Container, signalData?: any) => {
    const { value } = ensureFreeSpinsCounterTexts(container)
    value.text = getFreeSpinsCounterText(signalData)
}

export const formatFsAmount = (value: number): string => {
    if (!Number.isFinite(value)) {
        return '0.00'
    }

    return toMajorUnits(value).toFixed(2)
}

export const clearFreeSpinsTopTweens = () => {
    if (fsTumbleTween) {
        fsTumbleTween.kill()
        fsTumbleTween = null
    }

    if (fsTotalTween) {
        fsTotalTween.kill()
        fsTotalTween = null
    }

    if (fsMultiplierTween) {
        fsMultiplierTween.kill()
        fsMultiplierTween = null
    }

    if (fsMultiplierFlyTweens.length) {
        fsMultiplierFlyTweens.forEach((t) => t.kill())
        fsMultiplierFlyTweens = []
    }
}

export const resetFreeSpinsTopDisplayedValues = () => {
    fsDisplayedTumbleWin = 0
    fsDisplayedTotalWin = 0
    fsDisplayedMultiplier = 1
}

export const ensureFreeSpinsTopInfoTexts = (container: PIXI.Container) => {
    const ensureText = (name: string, text: string, x: number, y: number, isValue: boolean): PIXI.Text => {
        let textField = container.getChildByName(name) as PIXI.Text

        if (!textField) {
            textField = new PIXI.Text(text, {
                fontFamily: 'Montserrat_Bold',
                fontSize: isValue ? 53 : 24,
                fill: isValue ? 0xfff27a : 0xffffff,
                stroke: {
                    color: isValue ? '#5d0115' : '#3f1f02',
                    width: isValue ? 8 : 5,
                },
                letterSpacing: isValue ? 0 : 1,
            })
            textField.name = name
            textField.anchor.set(0.5)
            textField.position.set(x, y)
            container.addChild(textField)
        }

        return textField
    }

    const tumbleLabel = ensureText(FS_TOP_TUMBLE_LABEL_CHILD_NAME, 'TUMBLE WIN', 300, 92, false)
    const tumbleValue = ensureText(FS_TOP_TUMBLE_VALUE_CHILD_NAME, '0.00', 293, 195, true)
    const multiplierLabel = ensureText(FS_TOP_MULTIPLIER_LABEL_CHILD_NAME, 'MULTIPLIER', 500, 92, false)
    const multiplierValue = ensureText(FS_TOP_MULTIPLIER_VALUE_CHILD_NAME, '1x', 510, 195, true)
    const totalLabel = ensureText(FS_TOP_TOTAL_LABEL_CHILD_NAME, 'TOTAL WIN', 900, 92, false)
    const totalValue = ensureText(FS_TOP_TOTAL_VALUE_CHILD_NAME, '0.00', 1018, 195, true)

    return { tumbleLabel, tumbleValue, multiplierLabel, multiplierValue, totalLabel, totalValue }
}

export const playFsMultiplierCollectFly = (
    container: PIXI.Container,
    fromX: number,
    fromY: number,
    multiplier: number
) => {
    if (!isFreeSpins() || isPortrait() || !Number.isFinite(multiplier) || multiplier <= 0) {
        return
    }

    const { multiplierValue } = ensureFreeSpinsTopInfoTexts(container)
    const fromLocal = container.toLocal(new PIXI.Point(fromX, fromY))
    const targetLocal = container.toLocal(multiplierValue.getGlobalPosition(new PIXI.Point()))

    const textureName = `x${normalizeMultiplierTextureValue(Math.round(multiplier))}.png`
    const texture = PIXI.Texture.from(textureName)

    let token: PIXI.Sprite | PIXI.Text

    if (texture && texture !== PIXI.Texture.EMPTY) {
        const tokenSprite = new PIXI.Sprite(texture)
        tokenSprite.anchor.set(0.5)
        tokenSprite.scale.set(1)
        token = tokenSprite
    } else {
        const tokenText = new PIXI.Text(`${Math.round(multiplier)}x`, {
            fontFamily: 'Montserrat_Bold',
            fontSize: 76,
            fill: 0xfff27a,
            stroke: {
                color: '#5d0115',
                width: 11,
            },
        })
        tokenText.anchor.set(0.5)
        token = tokenText
    }

    token.position.set(fromLocal.x, fromLocal.y)
    token.alpha = 1
    container.addChild(token)

    const travelTween = gsap.to(token, {
        duration: 0.55,
        ease: 'power2.inOut',
        x: targetLocal.x,
        y: targetLocal.y,
        onComplete: () => {
            fsMultiplierCollectPreview += multiplier
            fsCurrentSpinMultiplier = Math.max(1, fsMultiplierCollectPreview)

            updateFreeSpinsTopInfoTexts(container, undefined, {
                animateMultiplier: true,
                duration: 0.2,
            })

            const fadeTween = gsap.to(token, {
                duration: 0.2,
                alpha: 0,
                onComplete: () => {
                    if (token.parent) {
                        token.parent.removeChild(token)
                    }
                    fsMultiplierFlyTweens = fsMultiplierFlyTweens.filter((t) => t !== travelTween && t !== fadeTween)
                },
            })

            fsMultiplierFlyTweens.push(fadeTween)
        },
    })

    fsMultiplierFlyTweens.push(travelTween)
}

export const updateFreeSpinsTopInfoTexts = (
    container: PIXI.Container,
    _signalData?: any,
    options: { animateTumble?: boolean; animateTotal?: boolean; animateMultiplier?: boolean; duration?: number } = {}
) => {
    const { tumbleValue, multiplierValue, totalValue } = ensureFreeSpinsTopInfoTexts(container)
    const targetTumble = fsCurrentSpinTumbleWin
    const targetMultiplier = Math.max(1, fsCurrentSpinMultiplier)
    const targetTotal = fsCumulativeTotalWin
    const duration = options.duration ?? FS_TOP_COUNTUP_DURATION

    if (options.animateTumble) {
        if (fsTumbleTween) {
            fsTumbleTween.kill()
            fsTumbleTween = null
        }

        const counter = { value: fsDisplayedTumbleWin }
        fsTumbleTween = gsap.to(counter, {
            duration,
            ease: FS_TOP_COUNTUP_EASING,
            value: targetTumble,
            onUpdate: () => {
                tumbleValue.text = formatFsAmount(counter.value)
                fsDisplayedTumbleWin = counter.value
            },
            onComplete: () => {
                fsDisplayedTumbleWin = targetTumble
                fsTumbleTween = null
            },
        })
    } else {
        fsDisplayedTumbleWin = targetTumble
        tumbleValue.text = formatFsAmount(targetTumble)
    }

    if (options.animateMultiplier) {
        if (fsMultiplierTween) {
            fsMultiplierTween.kill()
            fsMultiplierTween = null
        }

        const counter = { value: fsDisplayedMultiplier }
        fsMultiplierTween = gsap.to(counter, {
            duration: 0.35,
            ease: FS_TOP_COUNTUP_EASING,
            value: targetMultiplier,
            onUpdate: () => {
                multiplierValue.text = `${Math.max(1, Math.round(counter.value))}x`
                fsDisplayedMultiplier = counter.value
            },
            onComplete: () => {
                fsDisplayedMultiplier = targetMultiplier
                fsMultiplierTween = null
            },
        })
    } else {
        fsDisplayedMultiplier = targetMultiplier
        multiplierValue.text = `${targetMultiplier}x`
    }

    if (options.animateTotal) {
        if (fsTotalTween) {
            fsTotalTween.kill()
            fsTotalTween = null
        }

        const counter = { value: fsDisplayedTotalWin }
        fsTotalTween = gsap.to(counter, {
            duration,
            ease: FS_TOP_COUNTUP_EASING,
            value: targetTotal,
            onUpdate: () => {
                totalValue.text = formatFsAmount(counter.value)
                fsDisplayedTotalWin = counter.value
            },
            onComplete: () => {
                fsDisplayedTotalWin = targetTotal
                fsTotalTween = null
                GameSignals.fsTopTotalUpdated.emit()
            },
        })
    } else {
        fsDisplayedTotalWin = targetTotal
        totalValue.text = formatFsAmount(targetTotal)
    }
}

// --- Callback wrappers (called directly by component events) ---

export const onFsWinFrameTopShowFreeSpins = (element: PIXI.Container) => {
    ensureFreeSpinsTopInfoTexts(element)
    if (isFreeSpins()) {
        clearFreeSpinsTopTweens()
        fsCumulativeTotalWin = 0
        fsDisplayedTotalWin = 0
        fsCurrentSpinTumbleWin = 0
        fsDisplayedTumbleWin = 0
        fsCurrentSpinMultiplier = 1
        fsMultiplierCollectPreview = 0
        fsDisplayedMultiplier = 1
        updateFreeSpinsTopInfoTexts(element)
    }
    gsap.killTweensOf(element)
    element.alpha = 0
    element.visible = false
}

export const handleFsTopValuesFlow = (data: ICustomEventData) => {
    const flowData = data.signalData || {}
    const tumble = Number(flowData.tumble)
    const multiplier = Number(flowData.multiplier)
    const total = Number(flowData.total)

    if (!Number.isFinite(tumble) || !Number.isFinite(multiplier) || !Number.isFinite(total)) {
        return
    }

    fsCurrentSpinTumbleWin = tumble
    fsCurrentSpinMultiplier = Math.max(1, multiplier)
    fsMultiplierCollectPreview = fsCurrentSpinMultiplier > 1 ? fsCurrentSpinMultiplier : 0
    fsCumulativeTotalWin = Math.max(0, total)

    updateFreeSpinsTopInfoTexts(data.component.componentElement as PIXI.Container, undefined, {
        animateTumble: !!flowData.animateTumble,
        animateMultiplier: !!flowData.animateMultiplier,
        animateTotal: !!flowData.animateTotal,
        duration: typeof flowData.duration === 'number' ? flowData.duration : undefined,
    })
}

export const handleFsTopMultiplierCollectFly = (data: ICustomEventData) => {
    const payload = data.signalData || {}
    const fromX = Number(payload.fromX)
    const fromY = Number(payload.fromY)
    const multiplier = Number(payload.multiplier)

    if (!Number.isFinite(fromX) || !Number.isFinite(fromY) || !Number.isFinite(multiplier)) {
        return
    }

    playFsMultiplierCollectFly(data.component.componentElement as PIXI.Container, fromX, fromY, multiplier)
}

export const handleFsWinFrameTopOnBaseGame = (element: PIXI.Container) => {
    clearFreeSpinsTopTweens()
    fsCurrentSpinTumbleWin = 0
    fsCurrentSpinMultiplier = 1
    fsMultiplierCollectPreview = 0
    fsCumulativeTotalWin = 0
    fsCurrentSpinTotalIncrement = 0
    resetFreeSpinsTopDisplayedValues()

    const names = [FS_TOP_TUMBLE_VALUE_CHILD_NAME, FS_TOP_MULTIPLIER_VALUE_CHILD_NAME, FS_TOP_TOTAL_VALUE_CHILD_NAME]

    names.forEach((name) => {
        const textField = element.getChildByName(name) as PIXI.Text
        if (textField) {
            textField.text = ''
        }
    })
}
